import { Button, makeStyles, Spinner, Text } from "@fluentui/react-components";
import * as React from "react";
import { flexColumn, intro } from "../../styles/GlobalStyles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchPublicRecord } from '../../util/FormUtils';
import { useGlobalDispatch } from "../../state/GlobalStateProvider";
import { PublicProduct } from "../../dto/PublicProduct";
import { TrialRequestDialog, TrialFormPrefill } from "./TrialRequestDialog";
import { CenteredContent } from "../../components/CenteredContent";
import { EnvironmentEntry } from "../../components/MultiEnvironmentInput";

const entitySet = "products";

const useStyles = makeStyles({
    container: {
        ...flexColumn,
        width: "100%"
    },
    intro
});

export const ProductPage: React.FC = React.memo(() => {
    const styles = useStyles();
    const { productId } = useParams();
    const [searchParams] = useSearchParams();
    const globalDispatch = useGlobalDispatch();
    const navigate = useNavigate();
    
    const [record, setRecord] = React.useState<PublicProduct>({} as PublicProduct);
    const [isRequestingTrial, setIsRequestingTrial] = React.useState(false);
    const [prefillData, setPrefillData] = React.useState<TrialFormPrefill | undefined>();

    React.useEffect(() => { fetchPublicRecord(productId, entitySet, setRecord, globalDispatch); }, [productId, globalDispatch]);

    // Parse prefill data from URL parameter
    React.useEffect(() => {
        const prefillParam = searchParams.get('prefill');
        
        if (prefillParam) {
            try {
                // Decode and parse JSON
                const decoded = decodeURIComponent(prefillParam);
                const parsed = JSON.parse(decoded) as TrialFormPrefill;
                
                // Validate the structure (basic sanitization)
                const sanitized = sanitizePrefillData(parsed);
                setPrefillData(sanitized);
                
                // Auto-open dialog if prefill data is present
                setIsRequestingTrial(true);
            } catch (e) {
                console.error('Invalid prefill data in URL:', e);
                // Silently fail - don't show error to user
                setPrefillData(undefined);
            }
        }
    }, [searchParams]);

    const onClose = React.useCallback((id?: string) => {
        setIsRequestingTrial(false);

        if (id)
        {
            navigate(`/public/trialRequests/${id}`);
        }
    }, [navigate]);

    if (!record.guid)
    {
        return (
            <CenteredContent>
                <Spinner />
            </CenteredContent>
        );
    }
    else
    {
        return (
            <>
                <TrialRequestDialog 
                    isOpen={isRequestingTrial} 
                    close={onClose} 
                    productId={productId!}
                    prefill={prefillData}
                />
                <div className={styles.container}>
                    <p>
                        <CenteredContent>
                            <Text className={styles.intro}>
                                {record?.name}
                            </Text>
                        </CenteredContent>
                    </p>
                    <p>
                        <CenteredContent>
                            <Button 
                                appearance="primary" 
                                onClick={() => setIsRequestingTrial(true)}
                            >
                                Request trial
                            </Button>
                        </CenteredContent>
                    </p>
                </div>
            </>
        );
    }
});

function sanitizePrefillData(data: any): TrialFormPrefill {
    const sanitized: TrialFormPrefill = {};

    if (data.emailAddress && typeof data.emailAddress === 'string') {
        // Basic email format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const trimmed = data.emailAddress.trim().substring(0, 254);
        if (emailRegex.test(trimmed)) {
            sanitized.emailAddress = trimmed;
        }
    }

    if (Array.isArray(data.environments)) {
        sanitized.environments = data.environments
            .filter((env: any) => {
                // Must have guid and label
                if (!env || typeof env !== 'object') return false;
                if (!env.identifier || typeof env.identifier !== 'string') return false;
                if (!env.type || typeof env.type !== 'string') return false;
                if (!env.name || typeof env.name !== 'string') return false;
                
                // Validate GUID format
                const guidRegex = /^[0-9a-fA-F]{8}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{4}-?[0-9a-fA-F]{12}$/;
                return guidRegex.test(env.identifier);
            })
            .slice(0, 10) // Max 10 entries
            .map((env: any) => ({
                identifier: env.identifier.trim().toLowerCase().substring(0, 36),
                type: env.type.trim(),
                name: env.name.trim().substring(0, 100) // Reasonable label length limit
            }) as EnvironmentEntry);
    }

    return sanitized;
}